import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { cn } from "@v2/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { supabaseV2 } from "../../lib/supabase";
import { buildRosterLookup, type EaLinkRosterRow } from "../../lib/ea-name";
import { aggregateMatchesStats } from "./aggregateMatchesStats";
import { MatchKpi } from "./MatchKpi";
import { MatchListTable } from "./MatchListTable";
import { PlayerAggTable } from "./PlayerAggTable";
import type { EaMatch } from "./types";

export function SeasonPlayoffTab({
  teamId,
  matches,
  leagueMatches,
  playoffMatches,
  eaClubId,
  playoffAchievements,
}: {
  teamId: string;
  matches: EaMatch[];
  leagueMatches: EaMatch[];
  playoffMatches: EaMatch[];
  eaClubId: string;
  playoffAchievements: any[];
}) {
  const { t } = useTranslation("mondofc");
  const [sortBy, setSortBy] = useState<"goals" | "assists" | "rating" | "mom" | "saves">("goals");
  const stats = aggregateMatchesStats(matches, eaClubId);
  const rosterQuery = useQuery({
    queryKey: ["ea-roster-link", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("players")
        .select("id, display_name, ea_sports_id, ea_account_handle")
        .eq("team_id", teamId);
      if (error) throw error;
      return (data || []) as EaLinkRosterRow[];
    },
    staleTime: 60_000,
  });
  const rosterLookup = buildRosterLookup(rosterQuery.data || []);
  const goalDiff = stats.goalsFor - stats.goalsAgainst;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <MatchKpi label={t("season.kpiTotal")} value={stats.total} hint={t("season.kpiTotalHint", { league: leagueMatches.length, playoff: playoffMatches.length })} />
          <MatchKpi label={t("friendly.kpiRecord")} value={t("friendly.kpiRecordValue", { w: stats.wins, d: stats.draws, l: stats.losses })} hint={t("friendly.kpiRecordHint", { pct: stats.winPct })} accent="is-accent" />
          <MatchKpi label={t("friendly.kpiGoalDiff")} value={(goalDiff >= 0 ? "+" : "") + goalDiff} hint={`${stats.goalsFor}/${stats.goalsAgainst}`} accent={goalDiff >= 0 ? "text-emerald-600" : "text-rose-600"} />
          <MatchKpi label={t("friendly.kpiGoalsPerMatch")} value={stats.total > 0 ? (stats.goalsFor / stats.total).toFixed(2) : "0"} hint={t("friendly.kpiConcededHint", { n: stats.total > 0 ? (stats.goalsAgainst / stats.total).toFixed(2) : "0" })} />
          <MatchKpi label={t("friendly.kpiCleanSheet")} value={stats.cleanSheets} hint={stats.total > 0 ? t("friendly.kpiCleanSheetHint", { pct: Math.round((stats.cleanSheets / stats.total) * 100) }) : ""} />
          <MatchKpi label={t("friendly.kpiAvgRating")} value={stats.avgRating > 0 ? stats.avgRating.toFixed(2) : "-"} hint={t("friendly.kpiAvgRatingHint")} />
        </CardContent>
      </Card>

      {playoffAchievements?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t("season.achievementsTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {playoffAchievements.map((p: any, idx: number) => (
              <div key={idx} className="flex justify-between text-sm">
                <span>{p.seasonName?.replace(/CLUBS_LEAGUE_/, "").replace(/_/g, " ")}</span>
                <span className="font-mono text-muted-foreground">{t("season.achievementsLine", { div: p.bestDivision, group: p.bestFinishGroup })}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("season.listTitle")}</CardTitle>
          <CardDescription>{t("season.listDesc", { count: matches.length })}</CardDescription>
        </CardHeader>
        <CardContent>
          <MatchListTable teamId={teamId} matches={matches} eaClubId={eaClubId} emptyText={t("season.listEmpty")} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-start justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base">{t("season.perfTitle")}</CardTitle>
            <CardDescription>{t("season.perfDesc", { count: stats.total })}</CardDescription>
          </div>
          <div className="inline-flex max-w-full overflow-x-auto rounded-full bg-raised p-1 text-sm">
            {(["goals", "assists", "rating", "mom", "saves"] as const).map((k) => (
              <button
                key={k}
                onClick={() => setSortBy(k)}
                className={cn("px-2 py-1 rounded font-medium", sortBy === k ? "bg-[hsl(var(--active))] text-foreground" : "text-muted-foreground")}
              >
                {k === "goals" ? t("friendly.sortGoals") : k === "assists" ? t("friendly.sortAssists") : k === "rating" ? t("friendly.sortRating") : k === "mom" ? t("friendly.sortMom") : t("friendly.sortSaves")}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <PlayerAggTable players={stats.players} sortBy={sortBy} rosterLookup={rosterLookup} />
        </CardContent>
      </Card>
    </div>
  );
}
